import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiAppLegacy, USER_ALARM_QUERY_KEY } from '@/shared/api';
import { toRawAlarm } from '@/shared/data-mappers';
import type { AlarmItem } from '@/shared/data-types';

/**
 * 알림 설정 수정 mutation — `PUT /users/me/alarm`. caller 가 토글 반영된 **전체 목록**을 넘긴다
 * (미노출 항목 보존은 매퍼·caller 책임).
 *
 * 낙관 업데이트: `onMutate` 가 alarm query 캐시를 새 목록으로 즉시 교체(스위치 즉각 반응),
 * `onError` 가 이전 스냅샷으로 롤백, `onSettled` 가 무효화로 서버 진실 재동기화. 이 방식으로
 * 호출처 feature 는 로컬 state 없이 query 만 읽는다. 후처리(토스트)는 caller 가 주입(SRP).
 */
export function useUpdateUserAlarmMutation() {
  const api = useApiAppLegacy();
  const queryClient = useQueryClient();

  return useMutation<void, Error, AlarmItem[], { previous?: AlarmItem[] }>({
    mutationFn: async (items) => {
      await api.user.updateAlarm(toRawAlarm(items));
    },
    onMutate: async (items) => {
      await queryClient.cancelQueries({ queryKey: USER_ALARM_QUERY_KEY });
      const previous = queryClient.getQueryData<AlarmItem[]>(USER_ALARM_QUERY_KEY);
      queryClient.setQueryData<AlarmItem[]>(USER_ALARM_QUERY_KEY, items);
      return { previous };
    },
    onError: (_error, _items, context) => {
      if (context?.previous) {
        queryClient.setQueryData(USER_ALARM_QUERY_KEY, context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: USER_ALARM_QUERY_KEY });
    },
  });
}
